import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
// import { useAuth } from './AuthContext'; // Unused - GameContext uses localStorage tokens directly

const GameContext = createContext();

export const useGames = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGames must be used within a GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const [games, setGames] = useState([]);
  const [sharedGames, setSharedGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Add unsaved changes state and navigation protection
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [navigationHandler, setNavigationHandler] = useState(null);

  // Fetch user's games
  const fetchGames = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch('/api/game-states', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`Failed to fetch games: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      setGames(data);
      return data;
    } catch (err) {
      console.error('❌ Error fetching games:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array since we don't depend on any props or state

  // Fetch a specific game
  const fetchGame = useCallback(async (gameId) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(`/api/game-states/${gameId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch game');
      }
      
      const game = await response.json();
      return game;
    } catch (err) {
      console.error('Error fetching game:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array

  // Save a new game
  const saveGame = async (gameData) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch('/api/game-states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(gameData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save game');
      }
      
      const savedGame = await response.json();
      setGames(prevGames => [savedGame, ...prevGames]);
      
      return savedGame;
    } catch (err) {
      console.error('Error saving game:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing game
  const updateGame = async (gameId, gameData) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(`/api/game-states/${gameId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(gameData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update game');
      }
      
      const updatedGame = await response.json();
      
      setGames(prevGames => 
        prevGames.map(game => 
          game.game_state_id === gameId ? updatedGame : game
        )
      );
      
      return updatedGame;
    } catch (err) {
      console.error('Error updating game:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Delete a game
  const deleteGame = async (gameId) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(`/api/game-states/${gameId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete game');
      }
      
      setGames(prevGames => prevGames.filter(game => game.game_state_id !== gameId));
      
      return true;
    } catch (err) {
      console.error('Error deleting game:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch shared games
  const fetchSharedGames = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch('/api/games/shared', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch shared games');
      }
      
      const data = await response.json();
      setSharedGames(data);
      return data;
    } catch (err) {
      console.error('Error fetching shared games:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Share a game publicly
  const shareGame = async (gameData) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch('/api/games/share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(gameData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to share game');
      }
      
      return await response.json();
    } catch (err) {
      console.error('Error sharing game:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Load game data from the user's games or public games
  const loadGameState = useCallback(async (gameId, isShared = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const url = isShared 
        ? `/api/games/shared/${gameId}` 
        : `/api/game-states/${gameId}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load ${isShared ? 'shared' : ''} game`);
      }
      
      const gameData = await response.json();
      return gameData;
    } catch (err) {
      console.error('Error loading game state:', err);
      setError(err.message);
      return null;
    }
  }, []); // Empty dependency array

  // Load initial data
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchGames();
    }
  }, [fetchGames]); // Added fetchGames to dependency array

  // Navigation protection functions
  const checkUnsavedChanges = useCallback((callback) => {
    if (hasUnsavedChanges && navigationHandler) {
      navigationHandler(callback);
    } else {
      callback();
    }
  }, [hasUnsavedChanges, navigationHandler]);

  const setUnsavedChangesState = useCallback((hasChanges) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  const setNavigationProtection = useCallback((handler) => {
    setNavigationHandler(() => handler);
  }, []);

  const value = {
    games,
    sharedGames,
    loading,
    error,
    fetchGames,
    fetchGame,
    saveGame,
    updateGame,
    deleteGame,
    fetchSharedGames,
    shareGame,
    loadGameState,
    // Navigation protection
    hasUnsavedChanges,
    setUnsavedChangesState,
    setNavigationProtection,
    checkUnsavedChanges
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export default GameContext; 